"use client";

import { c as _c } from "react/compiler-runtime";
import LanguageLinks, { type Language } from "@components/SiteShell/LanguageLinks";
import { Icon } from "@justhome/react-ui";
import useDisableScroll from "@lib/hooks/useDisableScroll";
import useKeydown from "@lib/hooks/useKeydown";
import { useEffect, useRef, useState } from "react";
import type { NavItem, ShellLink, SiteHeaderCopy } from ".";
const Chevron = t0 => {
  const $ = _c(5);
  const {
    open
  } = t0;
  const t1 = open ? "rotate(180deg)" : undefined;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      transform: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Icon type="Down" size="20" fill="currentColor" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2) {
    t4 = <span aria-hidden="true" className="inline-flex flex-none transition-transform duration-150" style={t2}>{t3}</span>;
    $[3] = t2;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
const Row = t0 => {
  const $ = _c(3);
  const {
    link
  } = t0;
  let t1;
  if ($[0] !== link.href || $[1] !== link.label) {
    t1 = link.href ? <a href={link.href} className="block py-2xs text-body-1 text-neutral-675 no-underline">{link.label}</a> : <span className="block py-2xs text-body-1 text-neutral-425">{link.label}</span>;
    $[0] = link.href;
    $[1] = link.label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const MobileMenu = t0 => {
  const $ = _c(65);
  const {
    nav,
    mark,
    language,
    pathname,
    alternateLanguageHref,
    copy
  } = t0;
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(-1);
  const [top, setTop] = useState(0);
  const [prevPathname, setPrevPathname] = useState(pathname);
  const root = useRef(null);
  const trigger = useRef(null);
  const panel = useRef(null);
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setOpen(false);
  }
  useDisableScroll(open);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(false);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useKeydown("Escape", t1);
  const keyboard = useRef(false);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const byKey = () => keyboard.current = true;
      const byPointer = () => keyboard.current = false;
      window.addEventListener("keydown", byKey, true);
      window.addEventListener("pointerdown", byPointer, true);
      return () => {
        window.removeEventListener("keydown", byKey, true);
        window.removeEventListener("pointerdown", byPointer, true);
      };
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const wasOpen = useRef(false);
  let t4;
  let t5;
  if ($[3] !== open) {
    t4 = () => {
      if (!open && wasOpen.current && keyboard.current) {
        trigger.current?.focus();
      }
      wasOpen.current = open;
    };
    t5 = [open];
    $[3] = open;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== open) {
    t6 = () => {
      if (open) {
        panel.current?.focus();
      }
    };
    t7 = [open];
    $[6] = open;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[9] !== open) {
    t8 = () => {
      if (!open) {
        return;
      }
      const measure = () => {
        const header = root.current?.closest("header");
        setTop(header ? Math.max(0, header.getBoundingClientRect().top) : 0);
      };
      measure();
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    };
    t9 = [open];
    $[9] = open;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setOpen(true);
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const t11 = `${open ? "hidden" : "flex"} items-center justify-center w-m h-m p-none bg-transparent border-none cursor-pointer text-neutral-675 rounded-xs focusable`;
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Icon type="HamburgerButton" size="24" fill="currentColor" />;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== copy.menu || $[15] !== open || $[16] !== t11) {
    t13 = <button ref={trigger} type="button" aria-expanded={open} aria-controls="site-menu" aria-label={copy.menu} onClick={t10} className={t11}>{t12}</button>;
    $[14] = copy.menu;
    $[15] = open;
    $[16] = t11;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const t14 = !open;
  let t15;
  if ($[18] !== top) {
    t15 = {
      top
    };
    $[18] = top;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== mark) {
    t16 = <img src={mark} alt="" aria-hidden="true" className="h-3s w-auto block" />;
    $[20] = mark;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setOpen(false);
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Icon type="Close" size="24" fill="currentColor" />;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== copy.close) {
    t19 = <button type="button" aria-label={copy.close} onClick={t17} className="flex items-center justify-center w-m h-m p-none bg-transparent border-none cursor-pointer text-neutral-675 rounded-xs focusable">{t18}</button>;
    $[24] = copy.close;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== t16 || $[27] !== t19) {
    t20 = <div className="h-xl flex items-center justify-between">{t16}{t19}</div>;
    $[26] = t16;
    $[27] = t19;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  const t21 = copy.menu;
  let t22;
  if ($[29] !== expanded || $[30] !== nav) {
    let t23;
    if ($[32] !== expanded) {
      t23 = (item, index) => item.items ? <li key={`${item.label}-${index}`}><button type="button" aria-expanded={expanded === index} aria-controls={`site-menu-${index}`} onClick={() => setExpanded(expanded === index ? -1 : index)} className="w-full flex items-center justify-between gap-2xs px-none py-2s bg-transparent border-none cursor-pointer text-left text-body-1 font-semibold text-neutral-675 focusable">{item.label}<Chevron open={expanded === index} /></button><div id={`site-menu-${index}`} hidden={expanded !== index}><ul className="list-none p-none m-none pl-2xs pb-2s">{item.footer && <li><Row link={item.footer} /></li>}{item.items.map(_temp)}</ul></div></li> : <li key={`${item.label}-${index}`}><a href={item.href ?? "#"} className="block py-2s text-body-1 font-semibold text-neutral-675 no-underline">{item.label}</a></li>;
      $[32] = expanded;
      $[33] = t23;
    } else {
      t23 = $[33];
    }
    t22 = nav.map(t23);
    $[29] = expanded;
    $[30] = nav;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[34] !== t22) {
    t23 = <ul className="list-none p-none m-none divide-y divide-neutral-50">{t22}</ul>;
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== copy.menu || $[37] !== t23) {
    t24 = <nav aria-label={t21}>{t23}</nav>;
    $[36] = copy.menu;
    $[37] = t23;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== copy.phone) {
    let t26;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = /\s/g;
      $[41] = t26;
    } else {
      t26 = $[41];
    }
    t25 = copy.phone.replace(t26, "");
    $[39] = copy.phone;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  const t26 = `tel:${t25}`;
  let t27;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Icon type="PhoneTelephone" size="20" fill="currentColor" />;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] !== copy.phone || $[44] !== t26) {
    t28 = <a href={t26} className="h-2m flex items-center justify-center gap-2xs rounded-s border border-neutral-50 text-body-2 font-semibold text-neutral-675 no-underline focusable">{t27}{copy.phone}</a>;
    $[43] = copy.phone;
    $[44] = t26;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== copy.login || $[47] !== copy.loginHref) {
    t29 = <a href={copy.loginHref} className="h-2m flex items-center justify-center rounded-s border border-neutral-50 text-body-2 font-semibold text-neutral-675 no-underline focusable">{copy.login}</a>;
    $[46] = copy.login;
    $[47] = copy.loginHref;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  let t30;
  if ($[49] !== alternateLanguageHref || $[50] !== language) {
    t30 = <div className="flex justify-center pt-2xs"><LanguageLinks language={language} alternateLanguageHref={alternateLanguageHref} /></div>;
    $[49] = alternateLanguageHref;
    $[50] = language;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== t28 || $[53] !== t29 || $[54] !== t30) {
    t31 = <div className="pt-2s border-t border-neutral-50 flex flex-col gap-2xs">{t28}{t29}{t30}</div>;
    $[52] = t28;
    $[53] = t29;
    $[54] = t30;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  let t32;
  if ($[56] !== t14 || $[57] !== t15 || $[58] !== t20 || $[59] !== t24 || $[60] !== t31) {
    t32 = <div ref={panel} id="site-menu" tabIndex={-1} hidden={t14} style={t15} className="fixed left-none right-none bottom-none z-30 bg-neutral-0 overflow-y-auto px-s pb-3m focus:outline-none">{t20}{t24}{t31}</div>;
    $[56] = t14;
    $[57] = t15;
    $[58] = t20;
    $[59] = t24;
    $[60] = t31;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] !== t13 || $[63] !== t32) {
    t33 = <div ref={root} className="ml:hidden flex-none">{t13}{t32}</div>;
    $[62] = t13;
    $[63] = t32;
    $[64] = t33;
  } else {
    t33 = $[64];
  }
  return t33;
};
export default MobileMenu;
function _temp(child, childIndex) {
  return <li key={`${child.label}-${childIndex}`}><Row link={child} /></li>;
}